import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { SortColumn } from 'src/app/models/sortColumn.model';
import { StoreService } from 'src/app/services/infra/store/store.service';
import { ISelectOption } from '../../../selectOptions';
import { FilterTypesEnum } from '../../filterTypes.enum';
import { IPageFilters } from '../../interfaces/pageFilters.interface';
import { ISelectedFilters } from '../../selectedFilters.interface';
import { DateFilter } from '../filtersTypes/dateFilter.model';
import { DynamicMultiSelectFilters } from '../filtersTypes/dynamicFilter.model';
import { MultiSelectFilters } from '../filtersTypes/multiFilter.model';
import { FeatureFlagsService } from 'src/app/services/infra/featureFlags/feature-flags.service';
import { FiltersQueryTransformer } from 'src/app/services/filters/filtersQueryTransformer.ts/filtersQueryTransformer';
import { CreationTimeSinceOptions } from 'src/app/models/selectOptions/creationTimeSinceOptions';
import moment from 'moment';
import { AiSeveritySelectOptions } from 'src/app/models/selectOptions/AiSeveritySelectOptions.model';
import { OwaspNhiSelectOptions } from 'src/app/models/selectOptions/owaspNhiSelectOptions.model';
import { TextFilter } from '../filtersTypes/textFilter.model';
import { checkAllGivenIdsExistInOptions, fromIntegrationToSelectOption, fromLabelToSelectOption } from './utils';
import { AiAgentStepsHttpService } from 'src/app/services/httpServices/detection/aiAgentStepsHttpService/ai-agent-steps-http.service';
import { AiFindingsHttpService } from 'src/app/services/httpServices/detection/aiFindingsHttpService/ai-findings-http.service';
import { IntegrationModel } from 'src/app/models/api';
import { AutocompleteSearchResultsV2Model } from 'src/app/models/api/autocompleteSearchResultsV2.model';
import { aiFindingsTableColumns } from 'src/app/models/tableColumns/aiFindingsTableColumn';
import { ITableColumn } from 'src/app/models/tableColumns';
import { getActiveFindingAiServicesFromIntegrations } from 'src/app/common/integrationToFilterUtils';
import { getAiServiceLogoPath, getAiServiceDisplayName } from 'src/app/common/aiServiceUtils';
import { DetectionRuleSeverity, DetectionRuleTag } from 'src/app/models/api/finding.model';
import { RuleTagDisplayNamePipe } from 'src/app/pipes/rule-tag-display-name/rule-tag-display-name.pipe';
import { owaspLlmToDisplayName, owaspAsiToDisplayName } from 'src/app/common/violationRulesUtils';
import { findingAttackToDisplayName } from 'src/app/common/findingRulesUtils';
import { PermissionsService } from 'src/app/services/infra/permissionsService/permissions.service';
import { PERMISSIONS } from 'src/app/models/permissions/permissions.const';
import { ToggleOption } from 'src/app/components/option-toggle/option-toggle.component';
import { FeatureFlagTypes } from 'src/app/models/featureFlag/featureFlagsTypes.enum';
import {
  aidrAiServiceSelectOptions,
  aidrAiServiceSelectOptionsWithRsaDemo,
  inlineUnifiedAiServiceSelectOptions,
} from 'src/app/models/selectOptions/unifiedAiServiceSelectOptions.model';

export const NONE_LABEL_OPTION_ID = '__NONE__';

export const NONE_LABEL_OPTION: ISelectOption = {
  id: NONE_LABEL_OPTION_ID,
  value: NONE_LABEL_OPTION_ID,
  displayName: 'None',
  filterType: FilterTypesEnum.Label,
  categoryName: FilterTypesEnum.Label,
  withoutBeautifyDisplayName: true,
};

export class AiFindingsFiltersModel implements IPageFilters {
  public onUpdate$: BehaviorSubject<ISelectedFilters>;
  public allOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>([]);
  public severityFilters: MultiSelectFilters;
  public fromDate: DateFilter;
  public toDate: DateFilter;
  public actorFilters: DynamicMultiSelectFilters;
  public threadIdFilters: DynamicMultiSelectFilters;
  public creationTimeSinceDate: MultiSelectFilters;
  public owaspFilters: MultiSelectFilters;
  public owaspAsiFilters: MultiSelectFilters;
  public owaspNhiFilters: MultiSelectFilters;
  public search: TextFilter;
  public genAiMatrixFilters: MultiSelectFilters;
  public integrationFilters: MultiSelectFilters;
  public aiServiceFilters: MultiSelectFilters;
  public environmentFilters: DynamicMultiSelectFilters;
  public labelFilters: DynamicMultiSelectFilters;
  public tagFilters: MultiSelectFilters;
  public dataItemFilters: DynamicMultiSelectFilters;

  public readonly defaultSortColumn: ITableColumn | undefined = aiFindingsTableColumns.find(
    (column) => column.sortKey === 'timestamp'
  );
  public readonly sortedColumn$: BehaviorSubject<SortColumn | undefined> = new BehaviorSubject<SortColumn | undefined>(
    this.defaultSortColumn ? { column: this.defaultSortColumn, isDesc: true } : undefined
  );

  // Flag to track if we're navigating via deep link (from another page with specific filters)
  // When true, we skip applying default tag filters to avoid race condition
  private _isDeepLinkNavigation = false;

  // Toggle mode state for unified pipeline
  private _currentToggleMode: ToggleOption = 'option1';
  private _isUnifiedModeActive = false;

  constructor(
    private readonly _router: Router,
    private readonly _store: StoreService,
    private readonly _aiActivityHttpService: AiAgentStepsHttpService,
    private readonly _aiFindingsHttpService: AiFindingsHttpService,
    public featureFlagsService: FeatureFlagsService,
    private readonly _permissionsService: PermissionsService
  ) {
    this._initializeFilters();
    this._integrationsOnUpdate();
    this._getLabels();
    this._updateAiServiceFiltersFromIntegrations();
    this._initializeFilterOptions();
    this.onUpdate$ = new BehaviorSubject(this.getSelectedFilters());
  }

  public getSelectedFilters(): ISelectedFilters {
    return {
      fromDate: this.fromDate.getFilterValue(),
      toDate: this.toDate.getFilterValue(),
      severity: this.severityFilters.getSelectedFiltersValues(),
      actor: this.actorFilters.getSelectedFiltersValues(),
      threadId: this.threadIdFilters.getSelectedFiltersValues(),
      owaspLlm: this.owaspFilters.getSelectedFiltersValues(),
      owaspAsi: this.owaspAsiFilters.getSelectedFiltersValues(),
      owaspNhi: this.owaspNhiFilters.getSelectedFiltersValues(),
      search: this.search.getFilterValue(),
      genAiMatrix: this.genAiMatrixFilters.getSelectedFiltersValues(),
      integration: this.integrationFilters.getSelectedFiltersValues(),
      aiService: this.aiServiceFilters.getSelectedFiltersValues(),
      environment: this.environmentFilters.getSelectedFiltersValues(),
      label: this.labelFilters.getSelectedFiltersValues(),
      tag: this.tagFilters.getSelectedFiltersValues(),
      dataItem: this.dataItemFilters.getSelectedFiltersValues(),
    };
  }

  public getAllFiltersOptions(): BehaviorSubject<ISelectOption[]> {
    return this.allOptions$;
  }

  public getAllSelectedOptions(): ISelectOption[] {
    const selectedOptions = [
      ...this.creationTimeSinceDate.getSelectedOptions(),
      ...this.severityFilters.getSelectedOptions(),
      ...this.actorFilters.getSelectedOptions(),
      ...this.threadIdFilters.getSelectedOptions(),
      ...this.owaspFilters.getSelectedOptions(),
      ...this.owaspAsiFilters.getSelectedOptions(),
      ...this.owaspNhiFilters.getSelectedOptions(),
      ...this.genAiMatrixFilters.getSelectedOptions(),
      ...this.integrationFilters.getSelectedOptions(),
      ...this.aiServiceFilters.getSelectedOptions(),
      ...this.environmentFilters.getSelectedOptions(),
      ...this.labelFilters.getSelectedOptions(),
      ...this.tagFilters.getSelectedOptions(),
      ...this.dataItemFilters.getSelectedOptions(),
    ];

    return selectedOptions;
  }

  public updateFilterFromUserSelection(
    filterToUpdate: FilterTypesEnum | undefined,
    value: ISelectOption[] | Date | boolean | string | undefined
  ) {
    let needUpdate = false;
    if (filterToUpdate) {
      switch (filterToUpdate) {
        case FilterTypesEnum.FromDate:
          this.fromDate.update(value as Date | undefined);
          needUpdate = true;
          break;
        case FilterTypesEnum.ToDate:
          this.toDate.update(value as Date | undefined);
          needUpdate = true;
          break;
        case FilterTypesEnum.Search:
          this.search.update(value as string | undefined);
          needUpdate = true;
          break;
        default:
          break;
      }
    } else {
      if (value instanceof Array) {
        if (value.length == 0) {
          needUpdate = this.clearAllMultiSelectFiltersIfNeeded();
        } else {
          const optionsByCategory: {
            [key: string]: ISelectOption[];
          } = {};
          value.forEach((option) => {
            if (option.filterType) {
              if (option.filterType in optionsByCategory) {
                optionsByCategory[option.filterType].push(option);
              } else {
                optionsByCategory[option.filterType] = [option];
              }
            }
          });
          this._updateAllOptions();

          needUpdate =
            this.severityFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.FindingSeverity] || []) || needUpdate;
          needUpdate = this.actorFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.Actor] || []) || needUpdate;
          needUpdate =
            this.threadIdFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.ThreadId] || []) || needUpdate;
          needUpdate =
            this.owaspFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.OwaspLlm] || []) || needUpdate;
          needUpdate =
            this.owaspAsiFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.OwaspAsi] || []) || needUpdate;
          needUpdate =
            this.genAiMatrixFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.GenAiMatrix] || []) || needUpdate;
          needUpdate =
            this.integrationFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.Integration] || []) || needUpdate;
          needUpdate =
            this.aiServiceFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.AiService] || []) || needUpdate;
          needUpdate =
            this.environmentFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.Environment] || []) || needUpdate;
          needUpdate = this.labelFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.Label] || []) || needUpdate;
          needUpdate = this.tagFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.RuleTag] || []) || needUpdate;
        }
      }
    }
    if (needUpdate) {
      this._updateAllOptions();
      const filtersQuery = FiltersQueryTransformer.transformToQuery(this.getSelectedFilters());
      this._router.navigate([], {
        queryParams: {
          q: filtersQuery,
        },
        queryParamsHandling: 'merge',
      });
      this.onUpdate$.next(this.getSelectedFilters());
    }
  }

  public getFilterOptionsIfExist$(filter: FilterTypesEnum): BehaviorSubject<ISelectOption[]> {
    switch (filter) {
      case FilterTypesEnum.FindingSeverity:
        return this.severityFilters.getOptions$();
      case FilterTypesEnum.Actor:
        return this.actorFilters.getOptions$();
      case FilterTypesEnum.ThreadId:
        return this.threadIdFilters.getOptions$();
      case FilterTypesEnum.OwaspLlm:
        return this.owaspFilters.getOptions$();
      case FilterTypesEnum.OwaspAsi:
        return this.owaspAsiFilters.getOptions$();
      case FilterTypesEnum.OwaspNhi:
        return this.owaspNhiFilters.getOptions$();
      case FilterTypesEnum.GenAiMatrix:
        return this.genAiMatrixFilters.getOptions$();
      case FilterTypesEnum.Integration:
        return this.integrationFilters.getOptions$();
      case FilterTypesEnum.AiService:
        return this.aiServiceFilters.getOptions$();
      case FilterTypesEnum.Environment:
        return this.environmentFilters.getOptions$();
      case FilterTypesEnum.Label:
        return this.labelFilters.getOptions$();
      case FilterTypesEnum.RuleTag:
        return this.tagFilters.getOptions$();

      default:
        throw new Error(`AiFindingsFiltersModel getFilterOptionsIfExist: ${filter} not supported`);
    }
  }

  public removeSelectedOptionsIfExist(options: ISelectOption[]): void {
    const optionsIdsByCategory: { [key: string]: Set<string> } = {};
    options.forEach(
      (option) => {
        if (option.filterType) {
          if (option.filterType in optionsIdsByCategory) {
            optionsIdsByCategory[option.filterType].add(option.id);
          } else {
            optionsIdsByCategory[option.filterType] = new Set([option.id]);
          }
        }
      },
      this.getDynamicFilters().forEach((filter) => {
        optionsIdsByCategory[filter] = new Set();
      })
    );

    if (optionsIdsByCategory[FilterTypesEnum.FindingSeverity]) {
      this.severityFilters.updateIfNeeded(
        this.severityFilters
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.FindingSeverity] &&
              !optionsIdsByCategory[FilterTypesEnum.FindingSeverity].has(selectedOption.id)
          )
      );
    }

    if (optionsIdsByCategory[FilterTypesEnum.Actor]) {
      this.actorFilters.updateIfNeeded(
        this.actorFilters
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.Actor] &&
              !optionsIdsByCategory[FilterTypesEnum.Actor].has(selectedOption.id)
          )
      );
    }
    if (optionsIdsByCategory[FilterTypesEnum.ThreadId]) {
      this.threadIdFilters.updateIfNeeded(
        this.threadIdFilters
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.ThreadId] &&
              !optionsIdsByCategory[FilterTypesEnum.ThreadId].has(selectedOption.id)
          )
      );
    }
    if (optionsIdsByCategory[FilterTypesEnum.OwaspLlm]) {
      this.owaspFilters.updateIfNeeded(
        this.owaspFilters
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.OwaspLlm] &&
              !optionsIdsByCategory[FilterTypesEnum.OwaspLlm].has(selectedOption.id)
          )
      );
    }

    if (optionsIdsByCategory[FilterTypesEnum.OwaspAsi]) {
      this.owaspAsiFilters.updateIfNeeded(
        this.owaspAsiFilters
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.OwaspAsi] &&
              !optionsIdsByCategory[FilterTypesEnum.OwaspAsi].has(selectedOption.id)
          )
      );
    }

    if (optionsIdsByCategory[FilterTypesEnum.GenAiMatrix]) {
      this.genAiMatrixFilters.updateIfNeeded(
        this.genAiMatrixFilters
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.GenAiMatrix] &&
              !optionsIdsByCategory[FilterTypesEnum.GenAiMatrix].has(selectedOption.id)
          )
      );
    }

    if (optionsIdsByCategory[FilterTypesEnum.Integration]) {
      this.integrationFilters.updateIfNeeded(
        this.integrationFilters
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.Integration] &&
              !optionsIdsByCategory[FilterTypesEnum.Integration].has(selectedOption.id)
          )
      );
    }

    if (optionsIdsByCategory[FilterTypesEnum.AiService]) {
      this.aiServiceFilters.updateIfNeeded(
        this.aiServiceFilters
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.AiService] &&
              !optionsIdsByCategory[FilterTypesEnum.AiService].has(selectedOption.id)
          )
      );
    }

    if (optionsIdsByCategory[FilterTypesEnum.Environment]) {
      this.environmentFilters.updateIfNeeded(
        this.environmentFilters
          .getSelectedOptions()
          .filter((selectedOption) => !optionsIdsByCategory[FilterTypesEnum.Environment].has(selectedOption.id))
      );
    }

    if (optionsIdsByCategory[FilterTypesEnum.Label]) {
      this.labelFilters.updateIfNeeded(
        this.labelFilters
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.Label] &&
              !optionsIdsByCategory[FilterTypesEnum.Label].has(selectedOption.id)
          )
      );
    }

    if (optionsIdsByCategory[FilterTypesEnum.RuleTag]) {
      this.tagFilters.updateIfNeeded(
        this.tagFilters
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.RuleTag] &&
              !optionsIdsByCategory[FilterTypesEnum.RuleTag].has(selectedOption.id)
          )
      );
    }

    const filtersQuery = FiltersQueryTransformer.transformToQuery(this.getSelectedFilters());
    this._router.navigate([], {
      queryParams: {
        q: filtersQuery,
      },
      queryParamsHandling: 'merge',
    });
    this.onUpdate$.next(this.getSelectedFilters());
  }

  public getFilterSelectedIfExist(filter: FilterTypesEnum): string[] {
    switch (filter) {
      case FilterTypesEnum.FindingSeverity:
        return this.severityFilters.getSelectedFiltersIds();
      case FilterTypesEnum.Actor:
        return this.actorFilters.getSelectedFiltersIds();
      case FilterTypesEnum.ThreadId:
        return this.threadIdFilters.getSelectedFiltersIds();
      case FilterTypesEnum.OwaspLlm:
        return this.owaspFilters.getSelectedFiltersIds();
      case FilterTypesEnum.OwaspAsi:
        return this.owaspAsiFilters.getSelectedFiltersIds();
      case FilterTypesEnum.OwaspNhi:
        return this.owaspNhiFilters.getSelectedFiltersIds();
      case FilterTypesEnum.GenAiMatrix:
        return this.genAiMatrixFilters.getSelectedFiltersIds();
      case FilterTypesEnum.Integration:
        return this.integrationFilters.getSelectedFiltersIds();
      case FilterTypesEnum.AiService:
        return this.aiServiceFilters.getSelectedFiltersIds();
      case FilterTypesEnum.Environment:
        return this.environmentFilters.getSelectedFiltersIds();
      case FilterTypesEnum.Label:
        return this.labelFilters.getSelectedFiltersIds();
      case FilterTypesEnum.RuleTag:
        return this.tagFilters.getSelectedFiltersIds();

      default:
        throw new Error(`AiFindingsFiltersModel getFilterSelectedIfExist: ${filter} not supported`);
    }
  }

  public updateFiltersByKeyValue(keyValueFilters: { [key: string]: string[] }, updateQueryParams = false): void {
    let needUpdate = false;

    let values: string[] = [];

    // Check if this is a complete filter reset (empty object with no keys)
    // This happens when user clears all filters (?q=)
    const isCompleteReset = Object.keys(keyValueFilters).length === 0;

    // Check if this is a deep link with specific filters (filters present but severity/tag not included)
    // This happens when navigating from other pages with specific filters like dataItem, threadId, etc.
    const hasOtherFilters = Object.keys(keyValueFilters).length > 0;

    // Mark as deep link navigation to prevent async default filters from being applied
    if (hasOtherFilters) {
      this._isDeepLinkNavigation = true;
    }

    if ('fromDate' in keyValueFilters || this.fromDate.getFilterValue() !== undefined) {
      const fromDateMoment =
        keyValueFilters['fromDate'] && moment(keyValueFilters['fromDate'][0], 'x').isValid()
          ? moment(keyValueFilters['fromDate'][0], 'x')
          : null;

      // Reject future dates - set to undefined if date is in the future
      const fromDate = fromDateMoment && !fromDateMoment.isAfter(moment()) ? fromDateMoment.toDate() : undefined;

      this.fromDate.update(fromDate);
      needUpdate = true;
    }

    if ('toDate' in keyValueFilters || this.toDate.getFilterValue() !== undefined) {
      const toDateMoment =
        keyValueFilters['toDate'] && moment(keyValueFilters['toDate'][0], 'x').isValid()
          ? moment(keyValueFilters['toDate'][0], 'x')
          : null;

      // Reject future dates - set to undefined if date is in the future
      const toDate = toDateMoment && !toDateMoment.isAfter(moment()) ? toDateMoment.toDate() : undefined;

      this.toDate.update(toDate);
      needUpdate = true;
    }

    values = 'creationTimeSinceDate' in keyValueFilters ? keyValueFilters['creationTimeSinceDate'] : [];
    needUpdate = this.creationTimeSinceDate.updateByValuesIfNeeded(values) || needUpdate;

    // Handle severity filters: update if present, or clear if explicitly requested or if other filters are present
    // Defaults are applied only when clicking the left menu item "Findings" via query params
    if ('severity' in keyValueFilters) {
      values = keyValueFilters['severity'];
      needUpdate = this.severityFilters.updateByValuesIfNeeded(values) || needUpdate;
    } else if (isCompleteReset || hasOtherFilters) {
      // Clear severity when:
      // 1. Complete reset (user clicked "Clear all")
      // 2. Other filters are present but severity is not specified (deep linking from other pages)
      needUpdate = this.severityFilters.updateByValuesIfNeeded([]) || needUpdate;
    }
    // If no filters at all (empty navigation without explicit reset), preserve current severity

    values = 'threadId' in keyValueFilters ? keyValueFilters['threadId'] : [];
    if (values.length > 0 && !checkAllGivenIdsExistInOptions(this.threadIdFilters.getOptions$().value, values)) {
      this.threadIdFilters = new DynamicMultiSelectFilters([]);
      this._threadIdOnUpdate(values);
    }
    needUpdate = this.threadIdFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'owaspLlm' in keyValueFilters ? keyValueFilters['owaspLlm'] : [];
    needUpdate = this.owaspFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'owaspAsi' in keyValueFilters ? keyValueFilters['owaspAsi'] : [];
    needUpdate = this.owaspAsiFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'owaspNhi' in keyValueFilters ? keyValueFilters['owaspNhi'] : [];
    needUpdate = this.owaspNhiFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'genAiMatrix' in keyValueFilters ? keyValueFilters['genAiMatrix'] : [];
    needUpdate = this.genAiMatrixFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'integration' in keyValueFilters ? keyValueFilters['integration'] : [];
    needUpdate = this.integrationFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'aiService' in keyValueFilters ? keyValueFilters['aiService'] : [];
    needUpdate = this.aiServiceFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'environment' in keyValueFilters ? keyValueFilters['environment'] : [];
    if (values.length > 0 && !checkAllGivenIdsExistInOptions(this.environmentFilters.getOptions$().value, values)) {
      this.environmentFilters = new DynamicMultiSelectFilters([]);
      this._environmentOnUpdate(values);
    }
    needUpdate = this.environmentFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'actor' in keyValueFilters ? keyValueFilters['actor'] : [];
    if (values.length > 0 && !checkAllGivenIdsExistInOptions(this.actorFilters.getOptions$().value, values)) {
      this.actorFilters = new DynamicMultiSelectFilters([]);
      this._actorOnUpdate(values);
    }
    needUpdate = this.actorFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'label' in keyValueFilters ? keyValueFilters['label'] : [];
    needUpdate = this.labelFilters.updateByValuesIfNeeded(values) || needUpdate;

    // Handle tag filters: update if present, or clear if explicitly requested or if other filters are present
    // Defaults are applied only when clicking the left menu item "Findings" via query params
    if ('tag' in keyValueFilters) {
      values = keyValueFilters['tag'];
      needUpdate = this.tagFilters.updateByValuesIfNeeded(values) || needUpdate;
    } else if (isCompleteReset || hasOtherFilters) {
      // Clear tags when:
      // 1. Complete reset (user clicked "Clear all")
      // 2. Other filters are present but tag is not specified (deep linking from other pages)
      needUpdate = this.tagFilters.updateByValuesIfNeeded([]) || needUpdate;
    }
    // If no filters at all (empty navigation without explicit reset), preserve current tags

    values = 'dataItem' in keyValueFilters ? keyValueFilters['dataItem'] : [];
    const dataItemIds = values.map((v) => v.split('|')[0]);
    if (values.length > 0 && !checkAllGivenIdsExistInOptions(this.dataItemFilters.getOptions$().value, dataItemIds)) {
      this.dataItemFilters = new DynamicMultiSelectFilters([]);
      this._dataItemOnUpdate(values);
    }
    needUpdate = this.dataItemFilters.updateByValuesIfNeeded(dataItemIds) || needUpdate;

    if ('search' in keyValueFilters || this.search.getFilterValue() !== undefined) {
      this.search.update(keyValueFilters['search'] ? keyValueFilters['search'][0] : undefined);
      needUpdate = true;
    }

    if (needUpdate) {
      if (updateQueryParams) {
        const filtersQuery = FiltersQueryTransformer.transformToQuery(this.getSelectedFilters());
        this._router.navigate([], {
          queryParams: {
            q: filtersQuery,
          },
          queryParamsHandling: 'merge',
        });
      }
      this.onUpdate$.next(this.getSelectedFilters());
    }
  }

  public clearAllMultiSelectFiltersIfNeeded(): boolean {
    return (
      this.severityFilters.updateIfNeeded([]) ||
      this.actorFilters.updateIfNeeded([]) ||
      this.threadIdFilters.updateIfNeeded([]) ||
      this.owaspFilters.updateIfNeeded([]) ||
      this.owaspAsiFilters.updateIfNeeded([]) ||
      this.owaspNhiFilters.updateIfNeeded([]) ||
      this.genAiMatrixFilters.updateIfNeeded([]) ||
      this.integrationFilters.updateIfNeeded([]) ||
      this.aiServiceFilters.updateIfNeeded([]) ||
      this.environmentFilters.updateIfNeeded([]) ||
      this.labelFilters.updateIfNeeded([]) ||
      this.tagFilters.updateIfNeeded([]) ||
      this.dataItemFilters.updateIfNeeded([])
    );
  }

  public getDynamicFilters(): string[] {
    return [
      FilterTypesEnum.Actor,
      FilterTypesEnum.ThreadId,
      FilterTypesEnum.Environment,
      FilterTypesEnum.Label,
      FilterTypesEnum.DataItem,
    ];
  }

  /**
   * Sets the toggle mode and updates AI Service filter options accordingly.
   * This method is called when the dr_unified_pipeline FF is enabled and user switches between
   * Audit Log (option1) and Inline (option2) modes.
   *
   * @param toggle - 'option1' for Audit Log/AIDR mode, 'option2' for Inline mode
   * @param clearSelections - Whether to clear incompatible filter selections (default: true)
   */
  public setToggleMode(toggle: ToggleOption, clearSelections = true): void {
    if (!this.featureFlagsService.isFeatureEnabled(FeatureFlagTypes.UnifiedPipeline)) {
      return;
    }

    const modeChanged = this._currentToggleMode !== toggle;
    this._currentToggleMode = toggle;
    this._isUnifiedModeActive = true;

    if (toggle === 'option1') {
      const rsaDemo = this.featureFlagsService.isFeatureEnabled(FeatureFlagTypes.RsaDemoRuntimeBoundaries);
      this.aiServiceFilters.updateOptions(rsaDemo ? aidrAiServiceSelectOptionsWithRsaDemo : aidrAiServiceSelectOptions);
    } else {
      this.aiServiceFilters.updateOptions(inlineUnifiedAiServiceSelectOptions);
    }

    if (modeChanged && clearSelections) {
      this.aiServiceFilters.updateIfNeeded([]);
    }

    this._updateAllOptions();
  }

  /**
   * Returns the current toggle mode when unified pipeline is active.
   */
  public getCurrentToggleMode(): ToggleOption {
    return this._currentToggleMode;
  }

  /**
   * Returns whether unified mode is currently active.
   */
  public isUnifiedModeActive(): boolean {
    return this._isUnifiedModeActive;
  }

  public clearLabelFilters(): string | null {
    const needUpdate = this.labelFilters.updateIfNeeded([]);
    if (needUpdate) {
      this._updateAllOptions();
      this.onUpdate$.next(this.getSelectedFilters());
      return FiltersQueryTransformer.transformToQuery(this.getSelectedFilters());
    }
    return null;
  }

  public resetToDefaults(): void {
    // Reset severity to defaults (High + Medium)
    const defaultSeverityOptions = AiSeveritySelectOptions.filter(
      (opt) => opt.value === DetectionRuleSeverity.high || opt.value === DetectionRuleSeverity.medium
    );
    this.severityFilters.updateIfNeeded(defaultSeverityOptions);

    // Reset tags to defaults (GA + Preview) from dynamically loaded options
    const currentTagOptions = this.tagFilters.getOptions$().value;
    const defaultTagOptions = currentTagOptions.filter(
      (opt) => opt.value === DetectionRuleTag.generalAvailability || opt.value === DetectionRuleTag.preview
    );
    this.tagFilters.updateIfNeeded(defaultTagOptions);

    this._updateAllOptions();
    this.onUpdate$.next(this.getSelectedFilters());
  }

  private _initializeFilters() {
    this.fromDate = new DateFilter();
    this.toDate = new DateFilter();
    this.creationTimeSinceDate = new MultiSelectFilters(CreationTimeSinceOptions);
    // Initialize severity filters WITHOUT defaults - defaults are applied only on fresh navigation via component
    // Defaults are only applied when clicking the left menu item "Findings" via query params
    this.severityFilters = new MultiSelectFilters(AiSeveritySelectOptions);
    this.actorFilters = new DynamicMultiSelectFilters([]);
    this.threadIdFilters = new DynamicMultiSelectFilters([]);
    // OWASP LLM, OWASP ASI, and GenAI Attack filters initialized empty
    // Will be populated dynamically from API
    this.owaspFilters = new MultiSelectFilters([]);
    this.owaspAsiFilters = new MultiSelectFilters([]);
    this.owaspNhiFilters = new MultiSelectFilters(
      this.featureFlagsService.isFeatureEnabled(
        FeatureFlagTypes.SpmOwaspNhi
      )
        ? OwaspNhiSelectOptions
        : []
    );
    this.genAiMatrixFilters = new MultiSelectFilters([]);
    this.integrationFilters = new MultiSelectFilters([]);
    // AI Service filters will be initialized dynamically based on active integrations
    this.aiServiceFilters = new MultiSelectFilters([]);
    this.environmentFilters = new DynamicMultiSelectFilters([]);
    this.labelFilters = new DynamicMultiSelectFilters([]);
    // Initialize tag filters WITHOUT defaults - defaults are applied only on fresh navigation via component
    // Tag filters will be populated dynamically from API
    this.tagFilters = new MultiSelectFilters([]);
    this.dataItemFilters = new DynamicMultiSelectFilters([]);
    this.search = new TextFilter();
    this._updateAllOptions();
  }

  private _threadIdOnUpdate(threadIds: string[]) {
    const threadOptions: ISelectOption[] = [];
    threadIds.forEach((thread) => {
      threadOptions.push({
        id: thread,
        value: thread,
        displayName: thread,
        filterType: FilterTypesEnum.ThreadId,
        categoryName: FilterTypesEnum.ThreadId,
        withoutBeautifyDisplayName: true,
      } as ISelectOption);
    });

    const shouldUpdateSelectedOptions = this.threadIdFilters.updateOptions(Object.values(threadOptions));

    this._updateAllOptions();
    if (shouldUpdateSelectedOptions) {
      this.onUpdate$.next(this.getSelectedFilters());
    }
  }

  private _dataItemOnUpdate(dataItemValues: string[]) {
    const dataItemOptions = dataItemValues.map((value) => {
      const [id, encodedName] = value.split('|');
      const displayName = encodedName ? decodeURIComponent(encodedName) : id;

      return {
        id: id,
        value: id, // Use ID for API filtering and option matching
        displayName: displayName,
        filterType: FilterTypesEnum.DataItem,
        categoryName: FilterTypesEnum.DataItem,
        withoutBeautifyDisplayName: true,
      } as ISelectOption;
    });

    const shouldUpdateSelectedOptions = this.dataItemFilters.updateOptions(dataItemOptions);
    this._updateAllOptions();
    if (shouldUpdateSelectedOptions) {
      this.onUpdate$.next(this.getSelectedFilters());
    }
  }

  private _environmentOnUpdate(environmentsIds: string[]) {
    this._fetchAndUpdateFilterOptions(
      environmentsIds,
      'environment',
      FilterTypesEnum.Environment,
      this.environmentFilters
    );
  }

  private _actorOnUpdate(actorsIds: string[]) {
    this._fetchAndUpdateFilterOptions(actorsIds, 'mainActorDisplayName', FilterTypesEnum.Actor, this.actorFilters);
  }

  private _labelOnUpdate(labelsIds: string[]) {
    // Filter out __NONE__ as it's not a real label ID and shouldn't be fetched from backend
    const realLabelIds = labelsIds.filter((id) => id !== NONE_LABEL_OPTION_ID);
    if (realLabelIds.length > 0) {
      this._fetchAndUpdateFilterOptions(realLabelIds, 'label', FilterTypesEnum.Label, this.labelFilters);
    }
  }

  private _fetchAndUpdateFilterOptions(
    ids: string[],
    filterName: string,
    filterType: FilterTypesEnum,
    filters: DynamicMultiSelectFilters,
    transformDisplayName?: (text: string) => string
  ) {
    this._aiActivityHttpService
      .getDisplayNameById(filterName, ids)
      .subscribe((autocompleteResults: AutocompleteSearchResultsV2Model[]) => {
        const options: ISelectOption[] = autocompleteResults?.map((result) => {
          return {
            id: result.id,
            value: result.id,
            displayName: transformDisplayName ? transformDisplayName(result.resultText) : result.resultText,
            filterType: filterType,
            categoryName: filterType,
            withoutBeautifyDisplayName: true,
          } as ISelectOption;
        });

        const shouldUpdateSelectedOptions = filters.updateOptions(Object.values(options));

        this._updateAllOptions();
        if (shouldUpdateSelectedOptions) {
          this.onUpdate$.next(this.getSelectedFilters());
        }
      });
  }

  private _updateAllOptions() {
    this.allOptions$.next([
      ...this.severityFilters.getOptions$().value,
      ...this.actorFilters.getOptions$().value,
      ...this.threadIdFilters.getOptions$().value,
      ...this.owaspFilters.getOptions$().value,
      ...this.owaspAsiFilters.getOptions$().value,
      ...this.owaspNhiFilters.getOptions$().value,
      ...this.genAiMatrixFilters.getOptions$().value,
      ...this.integrationFilters.getOptions$().value,
      ...this.aiServiceFilters.getOptions$().value,
      ...this.environmentFilters.getOptions$().value,
      ...this.labelFilters.getOptions$().value,
      ...this.tagFilters.getOptions$().value,
      ...this.dataItemFilters.getOptions$().value,
    ]);
  }

  private _integrationsOnUpdate() {
    this._store.integrations$.subscribe((integrations: IntegrationModel[]) => {
      // Filter integrations based on user's access permissions for finding viewing
      const filteredIntegrations = this._permissionsService.filterIntegrationsByPermission(
        integrations,
        PERMISSIONS.FINDING_VIEWER
      );

      const integrationsPlatformSet: Set<string> = new Set();

      const integrationsOptionsMap: ISelectOption[] = filteredIntegrations.map((integration) => {
        integrationsPlatformSet.add(integration.platform);
        return fromIntegrationToSelectOption(integration);
      });

      const shouldUpdateSelectedOptions = this.integrationFilters.updateOptions(Object.values(integrationsOptionsMap));
      this._updateAllOptions();
      if (shouldUpdateSelectedOptions) {
        this.onUpdate$.next(this.getSelectedFilters());
      }
    });
  }

  private _getLabels() {
    this._store.labels$.subscribe((labels) => {
      const sortedLabels = labels.sort((a, b) => (b.id > a.displayName ? -1 : 1));
      const labelOptions = sortedLabels.map(fromLabelToSelectOption);

      const shouldUpdateSelectedOptions = this.labelFilters.updateOptions([NONE_LABEL_OPTION, ...labelOptions]);
      this._updateAllOptions();
      if (shouldUpdateSelectedOptions) {
        this.onUpdate$.next(this.getSelectedFilters());
      }
    });
  }

  /**
   * Dynamically updates AI Service filter options based on active integrations
   * Only shows AI services that are currently enabled for the customer
   *
   * Note: When unified mode is active, these dynamic updates are skipped because
   * setToggleMode() manages the filter options based on toggle state instead.
   */
  private _updateAiServiceFiltersFromIntegrations() {
    this._store.integrations$.subscribe((integrations) => {
      // Skip dynamic updates when unified mode is active - setToggleMode handles options
      if (this._isUnifiedModeActive) {
        return;
      }

      // Filter integrations based on user's access permissions for finding viewing
      const filteredIntegrations = this._permissionsService.filterIntegrationsByPermission(
        integrations,
        PERMISSIONS.FINDING_VIEWER
      );

      // Get active AI services from integrations
      const activeAiServices = getActiveFindingAiServicesFromIntegrations(filteredIntegrations);

      // Create filter options only for active services
      const aiServiceOptions: ISelectOption[] = Array.from(activeAiServices)
        .map((service) => ({
          id: `${FilterTypesEnum.AiService}-${service}`,
          value: service,
          displayName: getAiServiceDisplayName(service),
          filterType: FilterTypesEnum.AiService,
          categoryName: FilterTypesEnum.AiService.toString(),
          iconPath: getAiServiceLogoPath(service),
          withoutBeautifyDisplayName: true,
        }))
        .sort((a, b) => a.displayName.localeCompare(b.displayName));

      const shouldUpdateSelectedOptions = this.aiServiceFilters.updateOptions(aiServiceOptions);
      this._updateAllOptions();
      if (shouldUpdateSelectedOptions) {
        this.onUpdate$.next(this.getSelectedFilters());
      }
    });
  }

  private _initializeFilterOptions(): void {
    this._aiFindingsHttpService.getFilterValues().subscribe({
      next: (response) => {
        // Initialize rule tag filter options
        this._initializeRuleTagFilterOptions(response.ruleTags);

        // Initialize OWASP LLM filter options (filter by LLM-SEC prefix)
        const owaspLlmValues = response.owasp.filter((v) => v.startsWith('LLM-SEC-'));
        this._initializeOwaspLlmFilterOptions(owaspLlmValues);

        // Initialize OWASP ASI filter options (filter by ASI-SEC prefix)
        const owaspAsiValues = response.owasp.filter((v) => v.startsWith('ASI-SEC-'));
        this._initializeOwaspAsiFilterOptions(owaspAsiValues);

        // Initialize GenAI Attack Matrix filter options
        this._initializeGenAiMatrixFilterOptions(response.genAiAttack);

        this._updateAllOptions();
      },
      error: () => {
        // Silently fail - filters will remain empty
      },
    });
  }

  private _initializeRuleTagFilterOptions(ruleTags: string[]): void {
    // Get any pending filter values (stored as raw values when options were empty)
    const pendingValues = this.tagFilters.getSelectedFiltersIds();

    const tagOptions = this._transformToSelectOptions(ruleTags, FilterTypesEnum.RuleTag, (tag) =>
      new RuleTagDisplayNamePipe().transform(tag)
    );
    const shouldUpdateSelectedOptions = this.tagFilters.updateOptions(tagOptions);

    // Re-apply pending values now that options exist
    if (pendingValues.length > 0) {
      this.tagFilters.updateByValuesIfNeeded(pendingValues);
    } else if (this.tagFilters.getSelectedFiltersIds().length === 0 && !this._isDeepLinkNavigation) {
      // Apply defaults (GA + Preview) only if:
      // - No pending values
      // - No selected tags
      // - Not a deep link navigation (e.g., from policy page with specific filters)
      const defaultTags = tagOptions.filter(
        (opt) => opt.value === DetectionRuleTag.generalAvailability || opt.value === DetectionRuleTag.preview
      );
      if (defaultTags.length > 0) {
        this.tagFilters.updateIfNeeded(defaultTags);
      }
    }

    if (shouldUpdateSelectedOptions) {
      this.onUpdate$.next(this.getSelectedFilters());
    }
  }

  private _initializeOwaspLlmFilterOptions(values: string[]): void {
    // Get any pending filter values (stored as raw values when options were empty)
    const pendingValues = this.owaspFilters.getSelectedFiltersIds();

    const options = this._transformToSelectOptions(
      values,
      FilterTypesEnum.OwaspLlm,
      (value) => owaspLlmToDisplayName[value] || value,
      false
    );
    this.owaspFilters.updateOptions(options);

    // Re-apply pending values now that options exist
    if (pendingValues.length > 0) {
      this.owaspFilters.updateByValuesIfNeeded(pendingValues);
    }
  }

  private _initializeOwaspAsiFilterOptions(values: string[]): void {
    // Get any pending filter values (stored as raw values when options were empty)
    const pendingValues = this.owaspAsiFilters.getSelectedFiltersIds();

    const options = this._transformToSelectOptions(
      values,
      FilterTypesEnum.OwaspAsi,
      (value) => owaspAsiToDisplayName[value] || value,
      false
    );
    this.owaspAsiFilters.updateOptions(options);

    // Re-apply pending values now that options exist
    if (pendingValues.length > 0) {
      this.owaspAsiFilters.updateByValuesIfNeeded(pendingValues);
    }
  }

  private _initializeGenAiMatrixFilterOptions(values: string[]): void {
    // Get any pending filter values (stored as raw values when options were empty)
    const pendingValues = this.genAiMatrixFilters.getSelectedFiltersIds();

    const options = this._transformToSelectOptions(
      values,
      FilterTypesEnum.GenAiMatrix,
      (value) => {
        // API returns hyphens (T-2-4-1), mapping uses underscores (T_2_4_1)
        const normalizedKey = value.replace(/-/g, '_');
        return findingAttackToDisplayName[normalizedKey] || value;
      },
      false
    );
    this.genAiMatrixFilters.updateOptions(options);

    // Re-apply pending values now that options exist
    if (pendingValues.length > 0) {
      this.genAiMatrixFilters.updateByValuesIfNeeded(pendingValues);
    }
  }

  private _transformToSelectOptions(
    values: string[],
    filterType: FilterTypesEnum,
    displayNameFn: (value: string) => string,
    withoutBeautify = true
  ): ISelectOption[] {
    return values.map((value) => ({
      id: `${filterType}-${value}`,
      value: value,
      displayName: displayNameFn(value),
      categoryName: filterType.toString(),
      filterType: filterType,
      withoutBeautifyDisplayName: withoutBeautify,
    }));
  }
}
